import { Injectable } from '@angular/core';
import { LoggerService } from '@core/services/logger/logger.service';
import { TeethDiagramStore } from '@modules/teeth-diagram/state/teeth-diagram-store';
import { IdName } from '@shared/models/id-name';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { combineLatest, Observable, of } from 'rxjs';
import { filter, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { ProcedureOrderValues, SecondaryProcedureOrderForm } from './models/order-form';
import { OrderQuery } from './state/order-query';
import { OrderState, OrderStore } from './state/order-store';
import { Procedure } from '@shared/models/procedure';
import { ProcedureConfigurationService } from '@modules/order/services/procedure-configuration.service';
import { ProcedureMap } from '@shared/models/procedure-map';
import { ProcedureFormStateService } from '@shared/services/procedure-form-state.service';
import { ProcedureFormState } from '@shared/models/procedure-form-state';
import { OrderFormConfirmationService } from './services/order-form-confirmation.service';
import { SoftwareOptionsService } from '@shared/services/software-options.service';
import { ScanOptionsStore } from '@modules/scan-options/state/scan-options-store';
import { TreatmentStage } from '@shared/models/treatment-stage';
import { combineQueries, DirtyCheckPlugin } from '@datorama/akita';
import { SoftwareOptions } from '@shared/models/enums/enums';
import { ProcedureLoadRxService } from '@modules/order/services/procedure-load-rx.service';
import { DentureDetailsStore } from '@modules/denture-details/state/denture-details.store';
import { ApplianceDetailsStore } from '@modules/appliance-details/state/appliance-details.store';
import { ToothEditorStore } from '@modules/tooth-editor/state/tooth-editor-store';
import { TreatmentStageEnum } from '@modules/order/models/treatment-stage.enum';
import { ProcedureEnum } from '@core/procedure-helpers/models/procedure.enum';
import { SendToIdForNotLabsEnum, SendToTypeEnum } from './models/send-to-type.enum';
import { OrderFacadeBase } from '@modules/order/order.facade.base';
import { ShellStore } from '@shared/store/shell/shell-store';
import { RoleTypeEnum } from '@shared/models/role-type';
import { DefaultCrownStore } from '@modules/tooth-editor/state/default-crown-store';
import { AddRxService } from '@shared/services/add-rx.service';
import { TeethDiagramQuery } from '@modules/teeth-diagram/state/teeth-diagram-query';
import { ToothEditorQuery } from '@modules/tooth-editor/state/tooth-editor-query';
import { PopUpActions } from '@shared/models/enums/popup-modal-actions.enum';
import { SendToOption } from '@shared/models/send-to-option';
import { getSendToTypesFromOption } from '@shared/utils/send-to-utils';
import { CustomMaterialsService } from '@shared/services/custom-materials.service';
import { ProcedureSectionVisibilityService } from '@shared/services/procedure-section-visibility.service';
import { Maybe } from '@/types/globals';
import { OrthoModelICastService } from '@shared/services/ortho-model-icast.service';

@Injectable()
export class OrderV1Facade extends OrderFacadeBase {
	procedureMap$: Observable<ProcedureMap | null> = this.orderQuery.procedureMap$;
	procedureTreatmentStages$: Observable<Maybe<TreatmentStage[]>> = this.shellQuery.procedureTreatmentStages$;
	isReadOnly$: Observable<boolean> = this.shellQuery.getReadOnlyState('order');
	isReturned$: Observable<boolean> = this.shellQuery.isReturned$;

	get isProcedureAndTypeReadOnly$(): Observable<boolean> {
		return combineLatest([
			this.isReadOnly$,
			this.procedureMap$,
			this.shellQuery.isRxTakenForScan$,
			this.shellQuery.rx$,
			this.isReturned$,
			this.addRxService.isClonedRestorativeProcedure$
		]).pipe(
			map(([isReadOnly, procedureMap, isRxTakenForScan, rx, isReturned, isClonedRestorativeProcedure]) => {
				if (isClonedRestorativeProcedure) {
					return true;
				}

				if (isRxTakenForScan && !isReadOnly && !isReturned) {
					if (rx?.PrePrepScan) {
						return (procedureMap?.ProcedureId as ProcedureEnum) === ProcedureEnum.FixedRestorative;
					}

					if (rx?.DentureDetails?.IsDentureCopyScan) {
						return (procedureMap?.ProcedureId as ProcedureEnum) === ProcedureEnum.Denture_Removable;
					}
				}

				return isReadOnly || isReturned;
			})
		);
	}

	procedures$: Observable<Procedure[]> = this.procedureConfigurationService.procedures$;
	types$: Observable<IdName[]> = this.procedureConfigurationService.types$;
	availableLabs$: Observable<Maybe<IdName[]>> = this.procedureConfigurationService.sendToOptions$;

	/** Current store values for hydrating the form on mode switch (rx = null). */
	get storeState() {
		return {
			procedure: this.orderQuery.procedure,
			type: this.orderQuery.type,
			sendTo: this.orderQuery.sendTo,
			treatmentStage: this.orderQuery.treatmentStage,
			dueDate: this.orderQuery.dueDate,
			currentAlignerId: this.orderQuery.currentAlignerId
		};
	}

	iCastOrthoModelOnValue$: Observable<IdName | null> = this.orthoModelICastService.orthoModelICastValue$;
	isOrthoModelIcastEnabled$: Observable<boolean> = this.orthoModelICastService.isOrthoModelICastEnabled$;
	isIcastToggleDisabled$: Observable<boolean> = this.orthoModelICastService.isOrthoModelICastToggleDisabled('order');

	isCancellable$: Observable<boolean> = this.procedureMap$.pipe(
		map(currentProcedureMap =>
			this.orderQuery.availableProcedureMaps.some(
				pm =>
					(pm?.SendToTypes as SendToTypeEnum) === SendToTypeEnum.empty &&
					pm?.ProcedureId === currentProcedureMap?.ProcedureId &&
					pm?.TypeId === currentProcedureMap?.TypeId
			)
		)
	);

	procedureFormState$: Observable<ProcedureFormState> = combineQueries([
		this.procedureMap$,
		this.isReadOnly$,
		this.orderQuery.treatmentStage$,
		this.shellQuery.shouldValidateForSend$,
		this.shellQuery.proceduresMap$
	]).pipe(
		map(params => this.procedureFormStatusService.getFormState(...params)),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	isSendToIDSMessageVisible$: Observable<boolean> = this.sectionVisibilityService.isSendToIDSMessageVisible$;

	isIpaQrGeneratorVisible$: Observable<boolean> = combineQueries([
		this.softwareOptionsService.hasScannerSoftwareOption$(SoftwareOptions.QRCodeIPA),
		this.orderQuery.procedureMap$,
		this.shellQuery.userRole$
	]).pipe(
		map(
			([qrCodeIPAPresented, procedureMap, userRole]) =>
				qrCodeIPAPresented &&
				userRole === RoleTypeEnum.Doctor &&
				(procedureMap?.ProcedureId === (ProcedureEnum.Invisalign as number) ||
					procedureMap?.ProcedureId === (ProcedureEnum.StudyModel_iRecord as number))
		)
	);

	private teethDiagramDirtyCheck: DirtyCheckPlugin;

	userRole$: Observable<RoleTypeEnum> = this.shellQuery.userRole$;

	constructor(
		protected orderStore: OrderStore,
		protected orderQuery: OrderQuery,
		protected shellStore: ShellStore,
		protected shellQuery: ShellQuery,
		protected logger: LoggerService,
		private readonly orderFormConfirmationService: OrderFormConfirmationService,
		private readonly teethDiagramStore: TeethDiagramStore,
		protected teethDiagramQuery: TeethDiagramQuery,
		private readonly scanOptionsStore: ScanOptionsStore,
		private readonly procedureConfigurationService: ProcedureConfigurationService,
		private readonly procedureFormStatusService: ProcedureFormStateService,
		private readonly softwareOptionsService: SoftwareOptionsService,
		private readonly procedureLoadRxService: ProcedureLoadRxService,
		private readonly dentureDetailsStore: DentureDetailsStore,
		private readonly applianceDetailsStore: ApplianceDetailsStore,
		private readonly toothEditorStore: ToothEditorStore,
		protected toothEditorQuery: ToothEditorQuery,
		private readonly defaultCrownStore: DefaultCrownStore,
		private readonly addRxService: AddRxService,
		private readonly customMaterialsService: CustomMaterialsService,
		private readonly sectionVisibilityService: ProcedureSectionVisibilityService,
		private readonly orthoModelICastService: OrthoModelICastService
	) {
		super(orderStore, orderQuery, shellQuery, logger);

		this.teethDiagramDirtyCheck = new DirtyCheckPlugin(this.teethDiagramQuery).setHead();
	}

	handleProcedureChanged(procedureId: number): Observable<{ returnToProcedure?: IdName; procedureOrderValues?: ProcedureOrderValues }> {
		const currentProcedure = this.orderQuery.procedure;
		const currentProcedureId = currentProcedure?.Id;

		if (currentProcedureId === procedureId) {
			return of();
		}

		if (currentProcedureId) {
			// Adding dirty checks to everything can be changed by procedure change appeared to be a bit more tricky:
			// e.g. when selecting Denture/Removable this adds new section "Denture Details"
			// This is made via ngIf, so asynchronously related to this particular function.
			// "Denture Details" component in turn updates the denture details store on init,
			// effectively making it to always appear dirty for this current facade.
			// Leaving this for future enhancemnets
			return this.openResetConfirmationPopup(true).pipe(
				map(dialogResult => {
					if (dialogResult) {
						const procedureOrderValues = this.resetStateWithProcedure(procedureId);

						this.resetStoresDependingOnProcedure();

						// Still need to reset this checker though
						this.teethDiagramDirtyCheck.setHead();

						return { procedureOrderValues };
					}

					return { returnToProcedure: this.orderQuery.procedure };
				})
			);
		}

		return of({ procedureOrderValues: this.resetStateWithProcedure(procedureId) });
	}

	handleTypeChanged(typeId: number): Observable<{ shouldResetSendTo: boolean }> {
		const currentProcedureMap = this.orderQuery.procedureMap;
		const currentTypeId = currentProcedureMap?.TypeId;
		const currentSendTo = this.orderQuery.sendTo;

		if (!currentProcedureMap || typeId === currentTypeId) {
			return of({ shouldResetSendTo: false });
		}

		if (typeId !== currentTypeId) {
			this.dentureDetailsStore.update({ isUpperJawChecked: false, isLowerJawChecked: false });

			this.applianceDetailsStore.resetType();
		}

		let procedureMap = this.procedureConfigurationService.getProcedureMap(currentProcedureMap.ProcedureId, typeId, currentSendTo);

		if (!procedureMap) {
			procedureMap = this.procedureConfigurationService.getProcedureMap(currentProcedureMap.ProcedureId, typeId);
		}

		const availableLabs = this.procedureConfigurationService.getAvailableLabs({ procedureId: currentProcedureMap.ProcedureId, typeId });

		let canProceed$ = of(true);

		if ([ProcedureEnum.ImplantPlanning, ProcedureEnum.Denture_Removable].includes(procedureMap?.ProcedureId as ProcedureEnum)) {
			canProceed$ = this.openResetConfirmationPopup(this.teethDiagramDirtyCheck.isDirty()).pipe(
				tap(isAllowed => {
					if (isAllowed) {
						this.teethDiagramStore.resetTeeth();
						this.teethDiagramDirtyCheck.setHead();
					}
				})
			);
		}

		return canProceed$.pipe(
			map(isAllowed => {
				if (!isAllowed) {
					return { shouldResetSendTo: false };
				}

				if (availableLabs?.some(x => x?.Id === currentSendTo?.Id)) {
					this.orderStore.update({ procedureMap, availableLabs });

					return { shouldResetSendTo: false };
				} else {
					this.orderStore.update({ procedureMap, availableLabs, sendTo: null });

					return { shouldResetSendTo: true };
				}
			})
		);
	}

	handleSendToChanged(sendTo: Maybe<SendToOption>): Observable<Maybe<SendToOption>> {
		if (!!sendTo && sendTo?.Id === this.orderQuery.sendTo?.Id) {
			return of(this.orderQuery.sendTo);
		}

		const hasCustomEntitiesForCurrentLab =
			this.customMaterialsService.hasCustomEntitiesForLab(this.orderQuery.sendTo?.Id as Maybe<number>) ||
			this.customMaterialsService.hasCustomEntitiesForLab(sendTo?.Id as Maybe<number>);

		if (getSendToTypesFromOption(sendTo) === SendToIdForNotLabsEnum.glidewell || hasCustomEntitiesForCurrentLab) {
			return this.openResetConfirmationPopup(this.teethDiagramDirtyCheck.isDirty()).pipe(
				map(isProceedAndChange => {
					if (isProceedAndChange) {
						this.teethDiagramStore.resetTeeth();
						this.updateSendToStorage(sendTo);
						this.teethDiagramDirtyCheck.setHead();

						return sendTo;
					} else {
						return this.orderQuery.sendTo as SendToOption;
					}
				})
			);
		}

		this.updateSendToStorage(sendTo);

		return of(sendTo);
	}

	handleTreatmentStageChanges(treatmentStage: IdName): { shouldUpdateCurrentAligner: boolean; currentAligner?: Maybe<string> } {
		const currentTreatmentStage = this.orderQuery.treatmentStage;

		if (treatmentStage?.Id === currentTreatmentStage?.Id) {
			return { shouldUpdateCurrentAligner: false };
		}

		let newState: Partial<OrderState> = {
			treatmentStage
		};

		let shouldUpdateCurrentAligner = true;

		if (
			!treatmentStage ||
			(currentTreatmentStage?.Id === TreatmentStageEnum.InitialRecord && treatmentStage?.Id !== TreatmentStageEnum.InitialRecord)
		) {
			newState = { ...newState, currentAlignerId: null };
		} else if (
			currentTreatmentStage?.Id !== TreatmentStageEnum.InitialRecord &&
			treatmentStage?.Id === TreatmentStageEnum.InitialRecord
		) {
			newState = { ...newState, currentAlignerId: '0' };
		} else {
			shouldUpdateCurrentAligner = false;
		}

		this.orderStore.update(newState);

		return { shouldUpdateCurrentAligner, currentAligner: newState.currentAlignerId };
	}

	handleProcedureFlowValuesChanges(formValue: SecondaryProcedureOrderForm): void {
		const { dueDate, currentAlignerId } = formValue;

		this.orderStore.update({ dueDate, currentAlignerId });
	}
	// TODO: move to procedure-load service, refactor to return all values together
	loadProcedureFlowOrder(): Observable<ProcedureOrderValues> {
		return this.procedureLoadRxService
			.loadProcedureFlowOrder()
			.pipe(switchMap(rx => this.procedureLoadRxService.pullOrderSectionValues(rx)));
	}

	getDefaultProcedure(): Observable<Procedure | undefined> {
		// resetStateWithProcedure depends on async companies, so waiting for to be loaded before resetting the state
		return combineLatest([this.procedureConfigurationService.defaultProcedure$, this.shellQuery.companies$]).pipe(
			filter(([, companies]) => !!companies),
			map(([procedure]) => procedure),
			tap(procedure => {
				if (procedure) {
					this.resetStateWithProcedure(procedure.Id);
				}
			})
		);
	}

	setDirectToLab(): Observable<boolean> {
		return combineQueries([this.shellQuery.directToLabs$, this.orderQuery.procedureMap$, this.orderQuery.sendTo$]).pipe(
			filter(([directToLabs, procedureMap]) => !!directToLabs && !!procedureMap),
			map(([directToLabs, procedureMap, sendTo]) => this.calcDirectToLab(procedureMap?.CaseTypeId ?? 0, directToLabs, sendTo)),
			filter(directToLab => directToLab !== this.orderQuery.directToLab),
			tap(directToLab => {
				this.orderStore.update({ directToLab });
			})
		);
	}

	private static getProcedureBasedInitState(): Partial<OrderState> {
		return {
			treatmentStage: null,
			dueDate: null,
			currentAlignerId: null,
			sendTo: null,
			isMultiBiteSelected: undefined
		};
	}

	private openResetConfirmationPopup(condition: boolean) {
		return !condition
			? of(true)
			: this.orderFormConfirmationService.openPopUp$(true).pipe(map(dialogResult => dialogResult === PopUpActions.Ok));
	}

	private resetStateWithProcedure(procedureId: number): ProcedureOrderValues {
		const state = OrderV1Facade.getProcedureBasedInitState();
		const procedureMap = this.procedureConfigurationService.getProcedureMap(procedureId);
		const availableLabs = this.procedureConfigurationService.getAvailableLabs({ procedureId });

		this.orderStore.update({ ...state, procedureMap, availableLabs });
		if (!this.shellQuery.rxConfiguration?.Types) {
			this.logger.error(`Rx configuration is missing types ${JSON.stringify(this.shellQuery.rxConfiguration)}`, {
				module: 'procedure-configuration.service'
			});
		}

		const type = this.shellQuery.rxConfiguration?.Types?.find(x => x.Id === procedureMap?.TypeId);
		const sendTo =
			(state.sendTo?.Id ?? SendToTypeEnum.empty === (procedureMap?.SendToTypes as SendToTypeEnum)
				? state.sendTo
				: availableLabs?.[0]) ?? null;

		return {
			dueDate: state.dueDate,
			currentAlignerId: state.currentAlignerId,
			treatmentStage: state.treatmentStage,
			type,
			sendTo
		};
	}

	private resetStoresDependingOnProcedure(): void {
		this.scanOptionsStore.reset();
		this.dentureDetailsStore.reset();
		this.applianceDetailsStore.reset();
		this.teethDiagramStore.resetTeeth();
		this.toothEditorStore.reset();
		this.defaultCrownStore.reset();
		this.shellStore.updateProcedureChangedFlag(true);
		this.orderStore.updateIsDraft(false);
	}

	private updateSendToStorage(sendTo: Maybe<SendToOption>) {
		const { ProcedureId, TypeId } = this.orderQuery.procedureMap ?? {};
		// @ts-expect-error - TODO: fix this type error
		const procedureMap = this.procedureConfigurationService.getProcedureMap(ProcedureId, TypeId, sendTo);

		if (procedureMap?.Id !== this.orderQuery.procedureMap?.Id) {
			this.orderStore.update({ procedureMap, sendTo });
		} else {
			this.orderStore.update({ sendTo });
		}
	}
}
